import { Box } from "@mui/material";
import { Stack } from "@samdesk/components/Stack";
import useGetDashboard from "api/queries/useGetDashboard";
import layout from "common/layoutDefinitions";
import samdeskLogo from "images/samdeskLogo.png";
import { debounce } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import theme from "theme";
import { getTabPath } from "utils/tabUtils";
import Menu from "./Menu";
import NavTabs from "./NavTabs";
import Search from "./Search";
import { SeeAllTab } from "./SeeAllTab";
import useManageDeepLinkedTabs from "./useManageDeepLinkedTabs";

const NavBar = () => {
	const [numVisibleTabs, setNumVisibleTabs] = useState(0);

	const updateNumVisibleTabs = useMemo(
		() =>
			debounce(() => {
				const searchAndMenuWidth = 221;
				const navBarPadding = 56;
				const seeAllTabsButtonWidth = 50;

				const maxTabWidth = 208;

				const availableSpace =
					innerWidth -
					searchAndMenuWidth -
					navBarPadding -
					seeAllTabsButtonWidth;
				const numVisibleTabs = Math.floor(availableSpace / maxTabWidth);

				setNumVisibleTabs(Math.max(numVisibleTabs, 1)); // Always show at least one tab
			}, 200),
		[setNumVisibleTabs]
	);

	useEffect(() => {
		updateNumVisibleTabs();
	}, [updateNumVisibleTabs]);

	useEffect(() => {
		window.addEventListener("resize", updateNumVisibleTabs);

		return () => window.removeEventListener("resize", updateNumVisibleTabs);
	}, [updateNumVisibleTabs]);

	return (
		<Box sx={navBarContainerSx}>
			<TabBar numVisibleTabs={numVisibleTabs} />
			<Stack direction="row">
				<Search />
				<Box sx={actionsWrapper}>
					<Stack direction="row" alignItems="center">
						<img src={samdeskLogo} style={samdeskLogoStyle} />
						<Menu />
					</Stack>
				</Box>
			</Stack>
		</Box>
	);
};

export default NavBar;

const TabBar = ({ numVisibleTabs }: { numVisibleTabs: number }) => {
	const { pathname } = useLocation();
	const { data: dashboard } = useGetDashboard();

	useManageDeepLinkedTabs();

	const allTabs = useMemo(() => dashboard?.tabs ?? [], [dashboard?.tabs]);
	const visibleTabs = useMemo(
		() => allTabs.slice(0, numVisibleTabs),
		[allTabs, numVisibleTabs]
	);
	const showSeeAllTabsButton = allTabs.length > visibleTabs.length;

	const currentTabIndex = useMemo(
		() => allTabs.findIndex((tab) => pathname.includes(`/${getTabPath(tab)}`)),
		[allTabs, pathname]
	);
	const selectedTabInOverflowTabs = currentTabIndex + 1 > numVisibleTabs;

	return (
		<Stack direction="row" spacing="none">
			<NavTabs tabs={visibleTabs} currentTabIndex={currentTabIndex} />
			{showSeeAllTabsButton && (
				<SeeAllTab
					tabs={allTabs}
					currentTabIndex={currentTabIndex}
					highlightTab={selectedTabInOverflowTabs}
				/>
			)}
		</Stack>
	);
};

/** STYLES */

const samdeskLogoStyle = {
	width: "calc(var(--space-large) + var(--space-smallest))",
	height: "calc(var(--space-large)",
};

const actionsWrapper = {
	border: "1px solid var(--color-border)",
	paddingLeft: "var(--space-base)",
	marginBottom: "var(--space-small)",
	borderRadius: "var(--radius-base)",
};

const navBarContainerSx = {
	alignItems: "flex-end",
	backgroundColor: "background.default",
	borderBottom: `${layout.navBar.border}px solid ${theme.palette.border.main}`,
	display: "flex",
	height: `${layout.navBar.height}px`,
	justifyContent: "space-between",
	padding:
		"0 var(--space-base) 0 calc(var(--space-larger) + var(--space-small))",
	position: "relative",
};
